import React, { FC, ReactNode, useState } from 'react'
import { ThemeProvider, createTheme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import ButtonBase from '@mui/material/ButtonBase'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'

import { ink, prose } from './adminTokens'

// Scoped to this subtree on purpose, NOT set on the app theme.
//
// The player mounts MUI Tooltip, Menu and MenuItem (player/VideoPlayer.tsx),
// and those popups float over dark video. Anything put in the shared theme to
// suit a light admin page lands on them too — dark ink on a light card over a
// near-black 360 sphere. A nested provider gives the admin screen its own type
// and neutrals and reaches nothing else.
const adminTheme = (outer: any) =>
  createTheme(outer, {
    palette: {
      background: { default: ink.ground, paper: ink.card },
      text: { primary: ink.primary, secondary: ink.secondary },
      divider: ink.rule,
    },
    typography: {
      fontFamily: prose,
      h6: { fontSize: '0.9375rem', fontWeight: 600, letterSpacing: '-0.005em' },
      body1: { fontSize: '0.875rem' },
      body2: { fontSize: '0.8125rem' },
      caption: { fontSize: '0.71875rem' },
    },
    components: {
      // Sentence case throughout — verification/verificationTheme.ts states the
      // rule ("sentence case, no all-caps labels") and the rest of the product
      // already follows it.
      MuiButton: { styleOverrides: { root: { textTransform: 'none', fontWeight: 600 } } },
      MuiChip: { styleOverrides: { label: { fontWeight: 600 } } },
    },
  })

// The admin video screen used to be ten SectionWrappers stacked down one
// scroll. AdminShell is the frame that replaces that: a header carrying the
// video's identity and its two primary actions, a rail of four groups each
// showing its own one-line state, and one pane at a time.
//
// It owns no video knowledge at all — Video.tsx decides what the groups are,
// what each summary says and what goes in each pane. That separation is the
// point: adding a fifth group later is a change to Video.tsx, not to this.

export type PaneStatus = 'ok' | 'warn' | 'busy' | 'idle' | 'locked'

export interface AdminPane {
  id: string
  label: string
  /** One line under the label: the state of this group at a glance. */
  summary: string
  status: PaneStatus
  content: ReactNode
}

interface AdminShellProps {
  title: string
  /** Rendered under the title in monospace — the public URL, when there is one. */
  subtitle?: ReactNode
  /** Status chips beside the title. */
  chips?: ReactNode
  /** Primary actions, right-aligned in the header. */
  actions?: ReactNode
  panes: AdminPane[]
  /** Pinned to the bottom of the rail — the correction-window countdown, or the seal notice. */
  railFooter?: ReactNode
  onBack?: () => void
}

const DOT_COLOR: Record<Exclude<PaneStatus, 'locked'>, string> = {
  ok: ink.jade,
  warn: ink.alarm,
  busy: ink.jade,
  idle: ink.ruleStrong,
}

// Sits below the fixed 64px AppBar in pages/Layout.tsx.
const APP_BAR_HEIGHT = 64

const RailItem: FC<{ pane: AdminPane; selected: boolean; onSelect: () => void }> = ({
  pane,
  selected,
  onSelect,
}) => (
  <ButtonBase
    onClick={onSelect}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.4,
      px: 1.5,
      py: 1.25,
      borderRadius: '6px',
      justifyContent: 'flex-start',
      textAlign: 'left',
      bgcolor: selected ? ink.primary : 'transparent',
      '&:hover': { bgcolor: selected ? ink.primary : 'action.hover' },
    }}
  >
    {pane.status === 'locked' ? (
      <LockOutlinedIcon
        sx={{ fontSize: 15, flexShrink: 0, color: selected ? '#8FA3AC' : ink.tertiary }}
      />
    ) : (
      <Box
        sx={{
          width: 7,
          height: 7,
          mx: '4px',
          borderRadius: '50%',
          flexShrink: 0,
          bgcolor: DOT_COLOR[pane.status],
          // A running job is the one state the pane's own contents cannot
          // show while it is closed, so the dot says so.
          animation: pane.status === 'busy' ? 'adminRailPulse 1.4s ease-in-out infinite' : 'none',
          '@keyframes adminRailPulse': {
            '0%, 100%': { opacity: 1 },
            '50%': { opacity: 0.25 },
          },
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }}
      />
    )}
    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
      <Typography
        sx={{
          fontSize: '0.84375rem',
          fontWeight: 600,
          lineHeight: 1.35,
          color: selected ? '#FFFFFF' : ink.primary,
        }}
      >
        {pane.label}
      </Typography>
      <Typography
        sx={{
          fontSize: '0.71875rem',
          lineHeight: 1.35,
          color: selected ? '#8FA3AC' : ink.tertiary,
        }}
      >
        {pane.summary}
      </Typography>
    </Box>
  </ButtonBase>
)

const AdminShell: FC<AdminShellProps> = ({
  title,
  subtitle,
  chips,
  actions,
  panes,
  railFooter,
  onBack,
}) => {
  const [selectedId, setSelectedId] = useState(panes[0]?.id)

  // A pane can disappear between renders — Addendum only exists once sealed,
  // Versions only once there is a container. Falling back to the first pane
  // keeps the rail from selecting nothing.
  const current = panes.find((p) => p.id === selectedId) || panes[0]

  return (
    <ThemeProvider theme={adminTheme}>
    <Box
      sx={{
        border: `1px solid ${ink.rule}`,
        borderRadius: '8px',
        bgcolor: ink.card,
        overflow: 'hidden',
      }}
    >
      {/* header */}
      <Box
        sx={{
          bgcolor: ink.card,
          borderBottom: `1px solid ${ink.rule}`,
          px: 3,
          pt: 1.5,
          pb: 1.5,
        }}
      >
        {onBack && (
          <ButtonBase
            onClick={onBack}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              mb: 1,
              color: ink.secondary,
              fontSize: '0.78125rem',
            }}
          >
            <ChevronLeftIcon sx={{ fontSize: 16 }} />
            All videos
          </ButtonBase>
        )}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2.5 }}>
          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
              <Typography
                component="h1"
                title={title}
                sx={{
                  fontSize: '1.25rem',
                  fontWeight: 600,
                  letterSpacing: '-0.015em',
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {title}
              </Typography>
              {chips}
            </Box>
            {subtitle && <Box sx={{ mt: 0.75 }}>{subtitle}</Box>}
          </Box>
          {actions && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
              {actions}
            </Box>
          )}
        </Box>
      </Box>

      {/* rail + pane */}
      <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
        <Box
          sx={{
            flex: '0 0 244px',
            borderRight: `1px solid ${ink.rule}`,
            bgcolor: ink.card,
          }}
        >
          <Box
            sx={{
              position: 'sticky',
              // Sticks just below the global fixed AppBar. The header scrolls
              // away with the page, so there is no variable-height element to
              // offset past — which is exactly what used to hide the first
              // rail item behind a tall wrapped title.
              top: APP_BAR_HEIGHT + 16,
              display: 'flex',
              flexDirection: 'column',
              gap: '3px',
              p: 1.5,
            }}
          >
            {panes.map((pane) => (
              <RailItem
                key={pane.id}
                pane={pane}
                selected={pane.id === current?.id}
                onSelect={() => setSelectedId(pane.id)}
              />
            ))}
            {railFooter && (
              <Box sx={{ borderTop: `1px solid ${ink.rule}`, mt: 1.5, pt: 1.75, px: 1.5 }}>
                {railFooter}
              </Box>
            )}
          </Box>
        </Box>

        <Box sx={{ flexGrow: 1, minWidth: 0, bgcolor: ink.ground, p: 2.5 }}>
          {current?.content}
        </Box>
      </Box>
    </Box>
    </ThemeProvider>
  )
}

export default AdminShell
